import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Globe, ChatCircle, ArrowRight, Plus, Users, DownloadSimple, Eye } from "@phosphor-icons/react";
import axios from "axios";
import config from "../config";

const API = `${config.BACKEND_URL}/api`;

export default function Dashboard() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [websites, setWebsites] = useState([]);
  const [websiteStats, setWebsiteStats] = useState({});
  const [visitorStats, setVisitorStats] = useState({}); // 👈 NEW: visitor counts
  const [loading, setLoading] = useState(true);
  const [downloadingCsv, setDownloadingCsv] = useState(null);

  // ── existing: fetch conversation/message stats ────────────
  const fetchWebsiteStats = useCallback(async (websiteList, headers) => {
    const statsMap = {};
    await Promise.all(
      websiteList.map(async (w) => {
        try {
          const res = await axios.get(`${API}/analytics/${w.id}`, { headers });
          statsMap[w.id] = res.data;
        } catch {
          statsMap[w.id] = { total_conversations: 0, total_messages: 0 };
        }
      })
    );
    setWebsiteStats(statsMap);
  }, []);

  // ── NEW: fetch visitor stats (last 15 days) ───────────────
  const fetchVisitorStats = useCallback(async (websiteList, headers) => {
    const visMap = {};
    await Promise.all(
      websiteList.map(async (w) => {
        try {
          const res = await axios.get(
            `${API}/analytics/visitors?website_id=${w.id}&days=15`,
            { headers }
          );
          visMap[w.id] = {
            unique_visitors: res.data?.summary?.unique_visitors ?? 0,
            total_visits:    res.data?.summary?.total_visits    ?? 0,
          };
        } catch {
          visMap[w.id] = { unique_visitors: 0, total_visits: 0 };
        }
      })
    );
    setVisitorStats(visMap);
  }, []);

  useEffect(() => {
    const headers = getAuthHeaders();
    Promise.all([
      axios.get(`${API}/usage`,    { headers }),
      axios.get(`${API}/websites`, { headers }),
    ])
      .then(([usageRes, websitesRes]) => {
        setUsage(usageRes.data);
        const websiteList = websitesRes.data.websites || [];
        setWebsites(websiteList);
        fetchWebsiteStats(websiteList, headers);
        fetchVisitorStats(websiteList, headers); // 👈 NEW
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [getAuthHeaders, fetchWebsiteStats, fetchVisitorStats]);

  // ── CSV download (unchanged) ──────────────────────────────
  const handleDownloadCsv = async (e, websiteId, websiteName) => {
    e.stopPropagation();
    setDownloadingCsv(websiteId);
    try {
      const headers = getAuthHeaders();
      const res = await axios.get(`${API}/analytics/${websiteId}/export`, { headers });
      const messages = res.data.messages || [];

      if (messages.length === 0) {
        alert("No conversations found for this website.");
        return;
      }

      const csvRows = [
        ["Date", "Question", "Answer"],
        ...messages.map((m) => [
          new Date(m.created_at).toLocaleString(),
          `"${(m.question || "").replace(/"/g, '""')}"`,
          `"${(m.answer   || "").replace(/"/g, '""')}"`,
        ]),
      ];
      const csvContent = csvRows.map((r) => r.join(",")).join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url  = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href     = url;
      link.download = `${websiteName || websiteId}-conversations.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert("Failed to download CSV. Please try again.");
    } finally {
      setDownloadingCsv(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-sm font-mono uppercase tracking-[0.2em] text-[#6B7280]">Loading...</div>
      </div>
    );
  }

  const msgPercent = usage?.messages_limit > 0
    ? Math.min((usage.messages_used / usage.messages_limit) * 100, 100)
    : 0;
  const webPercent = usage?.websites_limit > 0
    ? Math.min((usage.websites_count / usage.websites_limit) * 100, 100)
    : 0;

  return (
    <div className="space-y-8" data-testid="dashboard-overview">

      {/* Header — unchanged */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Overview</h1>
          <p className="text-sm text-[#6B7280] mt-1">Your chatbot builder at a glance.</p>
        </div>
        <Button
          onClick={() => navigate("/websites")}
          className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-9 transition-transform active:scale-[0.98]"
          data-testid="dashboard-add-website-button"
        >
          <Plus size={16} weight="bold" className="mr-2" />
          Add Website
        </Button>
      </div>

      {/* Stats Grid — unchanged */}
      <div className="grid grid-cols-1 md:grid-cols-3 border border-[#E5E7EB]">
        <div className="p-8 border-b md:border-b-0 md:border-r border-[#E5E7EB]" data-testid="stat-messages">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Messages Used</div>
          <div className="font-heading text-4xl font-black tracking-tighter">
            {usage?.messages_used || 0}
            <span className="text-lg text-[#6B7280] font-normal">
              /{usage?.messages_limit === -1 ? "Unlimited" : usage?.messages_limit}
            </span>
          </div>
          {usage?.messages_limit > 0 && (
            <Progress value={msgPercent} className="mt-4 h-1.5 rounded-none" />
          )}
        </div>

        <div className="p-8 border-b md:border-b-0 md:border-r border-[#E5E7EB]" data-testid="stat-websites">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Websites</div>
          <div className="font-heading text-4xl font-black tracking-tighter">
            {usage?.websites_count || 0}
            <span className="text-lg text-[#6B7280] font-normal">/{usage?.websites_limit}</span>
          </div>
          <Progress value={webPercent} className="mt-4 h-1.5 rounded-none" />
        </div>

        <div className="p-8" data-testid="stat-plan">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A0A0A]/40 mb-4">Current Plan</div>
          <div className="font-heading text-4xl font-black tracking-tighter capitalize">
            {usage?.plan || "Free"}
          </div>
          {usage?.plan !== "pro" && (
            <Button
              onClick={() => navigate("/billing")}
              variant="outline"
              className="mt-4 rounded-none border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7] hover:text-white text-xs h-8"
              data-testid="dashboard-upgrade-button"
            >
              Upgrade to Pro
            </Button>
          )}
        </div>
      </div>

      {/* Recent Websites */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold">Recent Websites</h2>
          {websites.length > 0 && (
            <button
              onClick={() => navigate("/websites")}
              className="text-sm text-[#002FA7] hover:text-[#001F73] font-medium flex items-center gap-1"
              data-testid="view-all-websites-link"
            >
              View all <ArrowRight size={14} weight="bold" />
            </button>
          )}
        </div>

        {websites.length === 0 ? (
          <div className="border border-dashed border-[#D1D5DB] p-12 text-center" data-testid="empty-websites">
            <Globe size={40} weight="thin" className="mx-auto text-[#D1D5DB] mb-4" />
            <p className="text-sm text-[#6B7280] mb-4">No websites added yet. Add your first website to get started.</p>
            <Button
              onClick={() => navigate("/websites")}
              className="rounded-none bg-[#002FA7] hover:bg-[#001F73] text-white text-sm h-9"
              data-testid="empty-add-website-button"
            >
              Add Website
            </Button>
          </div>
        ) : (
          <div className="border border-[#E5E7EB]">
            {websites.slice(0, 5).map((w, i) => {
              const stats   = websiteStats[w.id] || {};
              const visitors = visitorStats[w.id] || {}; // 👈 NEW

              return (
                <div
                  key={w.id}
                  className={`flex items-center justify-between p-4 hover:bg-[#F9FAFB] transition-colors cursor-pointer ${
                    i < Math.min(websites.length, 5) - 1 ? "border-b border-[#E5E7EB]" : ""
                  }`}
                  onClick={() => navigate(`/chatbot/${w.id}`)}
                  data-testid={`website-row-${i}`}
                >
                  {/* Left — Website info */}
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 border border-[#E5E7EB] flex items-center justify-center flex-shrink-0">
                      <Globe size={20} weight="bold" className="text-[#6B7280]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold truncate">{w.name || w.url}</div>
                      <div className="text-xs text-[#6B7280] truncate">{w.url}</div>

                      {/* Analytics stats row — conversations + messages + visitors (NEW) */}
                      {w.status === "ready" && (
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-xs text-[#6B7280]">
                            <ChatCircle size={12} />
                            {stats.total_conversations ?? 0} conversations
                          </span>
                          <span className="flex items-center gap-1 text-xs text-[#6B7280]">
                            <Users size={12} />
                            {stats.total_messages ?? 0} messages
                          </span>
                          {/* 👇 NEW: visitor count — last 15 days */}
                          <span className="flex items-center gap-1 text-xs text-[#002FA7] font-medium">
                            <Eye size={12} />
                            {visitors.unique_visitors ?? 0} visitors
                            <span className="text-[#9CA3AF] font-normal">(15d)</span>
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right — Status + CSV button — unchanged */}
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-1 ${
                        w.status === "ready"
                          ? "bg-emerald-50 text-emerald-700"
                          : w.status === "error"
                          ? "bg-red-50 text-red-700"
                          : "bg-amber-50 text-amber-700"
                      }`}
                    >
                      {w.status}
                    </span>

                    {w.status === "ready" && (
                      <button
                        onClick={(e) => handleDownloadCsv(e, w.id, w.name || w.url)}
                        disabled={downloadingCsv === w.id}
                        className="flex items-center gap-1 text-xs text-[#002FA7] hover:text-[#001F73] border border-[#002FA7] hover:bg-[#002FA7] hover:text-white px-2 py-1 transition-colors disabled:opacity-50"
                        title="Download conversations as CSV"
                      >
                        <DownloadSimple size={12} />
                        {downloadingCsv === w.id ? "..." : "CSV"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
